import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { t as i18nT } from '../i18n';
import { useGoogleLogin, TokenResponse } from '@react-oauth/google';
import { AuthContextType, AuthState } from '../types/auth';
import authConfig from '../config/auth.json';
import { msalClient, initializeMsal, MSAL_ENABLED } from '../lib/msalClient';
import { InteractionRequiredAuthError, PopupRequest } from '@azure/msal-browser';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const normalizeUser = (user: any) => {
  if (!user || typeof user !== 'object') return user;
  const isAdmin = Boolean((user as any).is_admin);
  return { ...user, is_admin: isAdmin };
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
    error: null,
  });

  // Use the module-level translation helper (`i18nT`) instead of the hook here because
  // AuthProvider may be mounted before the I18nProvider in the app tree. The hook
  // requires the provider; the module helper reads a bound context when available.

  // Exchange access token for userinfo and validate
  // In development, use the Vite proxy to avoid CORS issues.
  // In production, use relative URLs.
  const apiPrefix = '/api';

  const refreshUserFromBackend = useCallback(async () => {
    try {
      const token = localStorage.getItem('bearer_token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const resp = await fetch(`${apiPrefix}/users/me`, { credentials: 'include', headers });
      if (!resp.ok) return null;
      const data = await resp.json();
      const user = normalizeUser(data.user || data);
      localStorage.setItem('auth_user', JSON.stringify(user));
      setState(prev => ({ ...prev, isAuthenticated: true, user, isLoading: false, error: null }));
      return user;
    } catch (err) {
      console.error('Background session validation failed:', err);
      return null;
    }
  }, [apiPrefix]);

  const fetchAndSetUser = async (token: string, provider: 'google' | 'microsoft', isIdToken = false) => {
    try {
      // Send token to backend to create/validate server session
      const body = isIdToken ? { id_token: token } : { access_token: token };
      const resp = await fetch(`${apiPrefix}/auth/${provider}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        // prefer explicit detail, fall back to error or status text
        const message = err?.detail || err?.error || resp.statusText || 'Server rejected token';
        throw new Error(message);
      }

      const user = normalizeUser(await resp.json());

      // Store JWT token if provided
      if (user.access_token) {
        localStorage.setItem('bearer_token', user.access_token);
      }

      // Persist minimal client-side info for UI only
      localStorage.setItem('auth_user', JSON.stringify(user));
      localStorage.setItem('auth_timestamp', Date.now().toString());

      setState({ isAuthenticated: true, user, isLoading: false, error: null });
      // Ensure we hydrate any missing fields (e.g. is_admin) without a full refresh.
      await refreshUserFromBackend();
    } catch (error: any) {
      console.error('Authentication error:', error);
      setState({ isAuthenticated: false, user: null, isLoading: false, error: error?.message || 'Authentication failed. Please try again.' });
    }
  };

  const checkExistingAuth = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      const storedUser = localStorage.getItem('auth_user');
      const storedTimestamp = localStorage.getItem('auth_timestamp');

      if (storedUser && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp);
        const now = Date.now();
        if (now - timestamp > authConfig.settings.sessionTimeout) {
          logout();
          return;
        }
        // Restore UI immediately from stored user for smooth UX
        try {
          const parsed = normalizeUser(JSON.parse(storedUser));
          setState({ isAuthenticated: true, user: parsed, isLoading: false, error: null });
        } catch (_) {
          setState(prev => ({ ...prev, isLoading: false }));
        }

        // Validate session with backend in background and update if valid.
        refreshUserFromBackend().catch(() => {});
      } else {
        setState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      console.error('Error checking existing auth:', error);
      // On error, clear stored tokens and ensure UI recovers without restart
      localStorage.removeItem('auth_user');
      localStorage.removeItem('bearer_token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_timestamp');
      setState(prev => ({ ...prev, isLoading: false, isAuthenticated: false, user: null, error: 'Error verifying existing session. Please sign in again.' }));
    }
  };

  useEffect(() => {
    checkExistingAuth();
  }, []);

  // useGoogleLogin returns a function that triggers the login flow
  const loginFn = useGoogleLogin({
    onSuccess: async (tokenResponse: TokenResponse) => {
      try {
        // `useGoogleLogin` may return either an access_token or a credential (id_token).
        // If a `credential` (id_token) is present, send it under `id_token` so the
        // backend can parse it as an ID token. Otherwise send as `access_token`.
        // tokenResponse may be a TokenResponse or an object with `credential`.
        // @ts-ignore
        const access_token = tokenResponse?.access_token as string | undefined;
        // @ts-ignore
        const credential = tokenResponse?.credential as string | undefined;
        if (!access_token && !credential) {
          setState({ isAuthenticated: false, user: null, isLoading: false, error: 'No access token or id_token received.' });
          return;
        }

        if (credential) {
          // send the id_token specifically
          await fetchAndSetUser(credential as string, 'google', true);
          // mark that this is an id_token by setting a special property on the fetch function
          // fetchAndSetUser will still post the token under the default key; we need to pass a flag
        } else {
          await fetchAndSetUser(access_token as string, 'google');
        }
      } catch (err) {
        console.error('Error in onSuccess handler:', err);
        setState(prev => ({ ...prev, isLoading: false, error: 'Login succeeded but processing failed. Please try again.' }));
      }
    },
    onError: (err?: any) => {
  console.error('Google login error:', err);
  setState(prev => ({ ...prev, isLoading: false, error: i18nT('login.google_failed', 'Google login failed or was cancelled.') }));
    },
    scope: authConfig.googleOAuth.scopes?.join(' ') || 'openid profile email',
  });

  const login = () => {
    setState(prev => ({ ...prev, error: null, isLoading: true }));
    try {
      loginFn();
    } catch (err) {
      console.error('Failed to start Google login flow:', err);
      setState(prev => ({ ...prev, isLoading: false, error: 'Failed to start login flow. Please reload or try again.' }));
    }
  };

  const logout = useCallback(() => {
    try {
      fetch(`${apiPrefix}/logout`, { method: 'GET', credentials: 'include' }).catch(() => {});
    } finally {
      localStorage.removeItem('auth_user');
      localStorage.removeItem('bearer_token');
      localStorage.removeItem('auth_timestamp');
      setState({ isAuthenticated: false, user: null, isLoading: false, error: null });
    }
  }, []);

  // Force clear stored auth and reset state - useful for recovery without full page reload
  const resetAuthState = () => {
    try {
      localStorage.removeItem('auth_user');
      localStorage.removeItem('bearer_token');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_timestamp');
    } catch (e) {
      console.error('Failed to clear local storage during reset:', e);
    }
    setState({ isAuthenticated: false, user: null, isLoading: false, error: null });
  };

  const checkAuth = () => {
    checkExistingAuth();
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    checkAuth,
    resetAuthState,
    loginWithMicrosoft: async () => {
      if (!MSAL_ENABLED) {
        console.warn('Microsoft login attempted but MSAL is disabled');
        setState(prev => ({ ...prev, isLoading: false, error: 'Microsoft sign-in is currently disabled.' }));
        return;
      }

      setState(prev => ({ ...prev, error: null, isLoading: true }));
      const request: PopupRequest = {
        scopes: authConfig.microsoftOAuth.scopes || ['User.Read'],
      };
      try {
        await initializeMsal();
        const loginResp = await msalClient.loginPopup(request);
        const account = loginResp.account || msalClient.getAllAccounts()[0];
        if (!account) throw new Error('No Microsoft account found after login');

        // Acquire token silently if possible
        const tokenResp = await msalClient.acquireTokenSilent({
          ...request,
          account,
        }).catch(async (e: unknown) => {
          if (e instanceof InteractionRequiredAuthError) {
            return msalClient.acquireTokenPopup(request);
          }
          throw e;
        });

        const accessToken = tokenResp.accessToken;
        if (!accessToken) throw new Error('No Microsoft access token');

        // Send token to backend to validate and create session
        await fetchAndSetUser(accessToken, 'microsoft');
      } catch (err: unknown) {
        console.error('Microsoft login error:', err);
        setState(prev => ({ ...prev, isLoading: false, error: i18nT('login.microsoft_failed', 'Microsoft login failed or was cancelled.') }));
      }
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
